/**
 * CheckinGateScreen — Iter 193.
 *
 * Extracted from the old global <CheckinGate>. Renders the full-screen
 * "content locked · check in first" takeover UI. Is now driven by
 * BatchAccessGate on a per-batch basis so a single batch's gate no
 * longer locks the entire portal.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  Lock, Flame, CircleCheck, Target, ListChecks, AlertTriangle,
  ArrowLeft, RefreshCw,
} from "lucide-react";

export default function CheckinGateScreen({ cfg, state, v2, batchName, onRefresh }) {
  // iter 197.9 — never hardcode a fallback for the daily MCQ target.
  // The admin configures this per batch and it varies (20 on default
  // batches, 40+ on serious cohorts like Path to 180). If cfg is
  // briefly missing during hydration we render the "target" language
  // without a specific number instead of silently lying with "20".
  const mcqTargetRaw = Number(cfg?.daily_mcq_target);
  const mcqTarget = Number.isFinite(mcqTargetRaw) && mcqTargetRaw > 0 ? mcqTargetRaw : null;
  const taskFloor = Number(cfg?.task_min_floor || 2);
  const activated = !!state?.activated;
  const hasWindow = !!v2?.window;
  const inWindow = !!v2?.in_window;
  return (
    <div className="min-h-screen w-full flex items-start justify-center px-4 md:px-10 py-8 md:py-14"
         style={{ background: "var(--px-bg)" }}
         data-testid="checkin-gate">
      <div className="w-full max-w-[860px]">
        <div
          className="rounded-[20px] p-5 md:p-6 mb-5 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(168,85,247,0.14), rgba(15,23,42,0))",
            border: "1px solid rgba(168,85,247,0.45)",
          }}
        >
          <div
            aria-hidden
            className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(168,85,247,0.20), transparent 70%)" }}
          />
          <div className="flex items-center gap-3 mb-3 relative">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "rgba(168,85,247,0.20)", border: "1.5px solid rgba(168,85,247,0.55)" }}
            >
              <Lock size={18} style={{ color: "#c084fc" }} strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: "#c084fc" }}>
                {batchName ? <>{batchName} · one-time activation</> : "Content locked · Activate to unlock"}
              </div>
              <h1 className="text-[22px] md:text-[26px] font-black leading-tight" style={{ color: "var(--px-ink)" }}>
                {batchName ? (
                  <>Activate <span style={{ color: "#a855f7" }}>{batchName}</span></>
                ) : (
                  "Do your first check-in to unlock"
                )}
              </h1>
            </div>
          </div>
          <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--px-ink-soft)" }}>
            {batchName ? (
              <>
                <b>{batchName}</b> requires a <b>one-time activation check-in</b> before content unlocks.
                Complete it once and this lock is lifted permanently — you&apos;ll never see this screen for
                <b> {batchName}</b> again, even after midnight rolls over.
                {" "}Your <b>other batches stay accessible</b> — this lock only applies to <b>{batchName}</b>.
              </>
            ) : !hasWindow ? (
              "This batch runs on strict accountability. Head to the Streak page to set your check-in window and complete your first check-in — after that, content stays unlocked."
            ) : !inWindow ? (
              "Your check-in window isn't open right now. Come back when it opens for your one-time activation check-in — content will unlock permanently after that."
            ) : (
              "Head to the Streak page and complete your first check-in. Content unlocks the moment you do — and stays unlocked afterwards."
            )}
          </p>
        </div>

        <div className="mb-5">
          <Link
            to="/student/streak"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] text-[13.5px] font-black tracking-[0.02em]"
            style={{ background: "#a855f7", color: "#0b1220" }}
            data-testid="gate-open-streak"
          >
            <Lock size={14} /> Go to Streak page
          </Link>
        </div>

        <div
          className="rounded-[16px] p-4 md:p-5 mb-5 flex gap-3"
          style={{
            background: activated ? "rgba(251,113,133,0.10)" : "rgba(56,189,248,0.14)",
            border: `1px solid ${activated ? "rgba(251,113,133,0.45)" : "rgba(14,165,233,0.55)"}`,
          }}
          data-testid="gate-strict-notice"
        >
          <AlertTriangle
            size={18}
            style={{ color: activated ? "#dc2626" : "#0284c7", flexShrink: 0, marginTop: 2 }}
          />
          <div className="min-w-0">
            {activated ? (
              <>
                <div className="font-bold text-[13.5px] mb-1" style={{ color: "#b91c1c" }}>
                  Skipping check-in does NOT pause the streak system.
                </div>
                <p className="text-[12.5px] leading-relaxed" style={{ color: "var(--px-ink-soft)" }}>
                  Even if you don&apos;t check in today, the day is still counted against you. Your current
                  streak will reset to <b>0</b>, and if this batch has auto-freeze enabled, your account may be
                  frozen. To keep your streak alive, complete <b>all three</b> criteria below every day.
                </p>
              </>
            ) : (
              <>
                <div className="font-bold text-[13.5px] mb-1" style={{ color: "#075985" }}>
                  Your streak hasn&apos;t started yet.
                </div>
                <p className="text-[12.5px] leading-relaxed" style={{ color: "var(--px-ink-soft)" }}>
                  Content stays locked until your <b>first check-in</b> — but nothing is being deducted right
                  now. No streak, no freeze. The moment you tap <b>Check in</b> above, Day 1 begins and the
                  streak system starts tracking your consistency from that point onward.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <Criterion
            icon={CircleCheck}
            title="Daily check-in"
            body="Tap Check in every day. This starts your day's race."
            color="#38bdf8"
          />
          <Criterion
            icon={ListChecks}
            title={"Complete your tasks"}
            body={`Set at least ${taskFloor} daily tasks for yourself, then tick each one off before day end.`}
            color="#22c55e"
          />
          <Criterion
            icon={Target}
            title={mcqTarget !== null ? `Solve ${mcqTarget} MCQs` : "Hit your daily MCQ target"}
            body={"Hit today's MCQ target through practice, tests or quizzes. All correct-or-attempted MCQs count."}
            color="#f97316"
          />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/student"
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-[10px] text-[12.5px] font-semibold"
            style={{ background: "rgba(148,163,184,0.10)", color: "var(--px-ink-soft)" }}
            data-testid="gate-home"
          >
            <ArrowLeft size={13} /> Back to dashboard
          </Link>
          <button
            onClick={onRefresh}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-[10px] text-[12.5px] font-semibold"
            style={{ background: "rgba(148,163,184,0.10)", color: "var(--px-ink-soft)" }}
            data-testid="gate-refresh"
          >
            <RefreshCw size={13} /> I&apos;ve checked in — refresh
          </button>
        </div>

        <div className="mt-6 text-center text-[11.5px]" style={{ color: "var(--px-ink-mute)" }}>
          <Flame size={11} className="inline mr-1" style={{ color: "#fb923c" }} />
          Consistency compounds. One day at a time.
        </div>
      </div>
    </div>
  );
}

function Criterion({ icon: Icon, title, body, color }) {
  return (
    <div
      className="rounded-[14px] p-4"
      style={{ background: "var(--px-surface)", border: `1px solid ${color}45` }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: `${color}20`, border: `1px solid ${color}55` }}
        >
          <Icon size={14} style={{ color }} />
        </div>
        <div className="font-bold text-[13px]" style={{ color: "var(--px-ink)" }}>{title}</div>
      </div>
      <p className="text-[12px] leading-relaxed" style={{ color: "var(--px-ink-soft)" }}>{body}</p>
    </div>
  );
}
